import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { superadminApi } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CURRENCIES, STRIPE_COUNTRIES } from '@/lib/money'
import { TIME_ZONES, DEFAULT_TIME_ZONE_FOR_COUNTRY } from '@/lib/dates'
import { X } from 'lucide-react'

interface Props {
  onClose: () => void
}

export default function CreateTenantModal({ onClose }: Props) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ tenantName: '', adminEmail: '', adminPassword: '', adminFirstName: '', adminLastName: '', currency: 'GBP', country: 'GB', timeZoneId: 'Europe/London' })
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => superadminApi.createTenant(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenants'] })
      onClose()
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail ?? err.response?.data ?? 'Something went wrong.')
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold text-gray-900 mb-5">Create tenant</h2>

        <form
          onSubmit={e => { e.preventDefault(); setError(null); mutation.mutate() }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="tenantName">Tenant name</Label>
            <Input
              id="tenantName"
              value={form.tenantName}
              onChange={e => setForm(f => ({ ...f, tenantName: e.target.value }))}
              placeholder="Acme Training Ltd"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="adminFirstName">Admin first name</Label>
              <Input
                id="adminFirstName"
                value={form.adminFirstName}
                onChange={e => setForm(f => ({ ...f, adminFirstName: e.target.value }))}
                placeholder="Jane"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adminLastName">Admin last name</Label>
              <Input
                id="adminLastName"
                value={form.adminLastName}
                onChange={e => setForm(f => ({ ...f, adminLastName: e.target.value }))}
                placeholder="Smith"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adminEmail">Admin email</Label>
            <Input
              id="adminEmail"
              type="email"
              value={form.adminEmail}
              onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))}
              placeholder="admin@acme.com"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adminPassword">Admin password</Label>
            <Input
              id="adminPassword"
              type="password"
              value={form.adminPassword}
              onChange={e => setForm(f => ({ ...f, adminPassword: e.target.value }))}
              placeholder="Min. 8 characters"
              minLength={8}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="currency">Currency</Label>
              <select
                id="currency"
                value={form.currency}
                onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="country">Country / region</Label>
              <select
                id="country"
                value={form.country}
                onChange={e => setForm(f => ({ ...f, country: e.target.value, timeZoneId: DEFAULT_TIME_ZONE_FOR_COUNTRY[e.target.value] ?? f.timeZoneId }))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {STRIPE_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>

          <div className="space-y-1.5">
            <Label htmlFor="timeZone">Time zone</Label>
            <select
              id="timeZone"
              value={form.timeZoneId}
              onChange={e => setForm(f => ({ ...f, timeZoneId: e.target.value }))}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {TIME_ZONES.map(z => <option key={z.id} value={z.id}>{z.label}</option>)}
            </select>
          </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
